import { useState } from "react";
import { motion } from "framer-motion";
import { computeTarifa, fmt$, fmtNum, RELEVANCIA } from "@/lib/calc";
import { trackLead, trackShareCard } from "@/lib/pixel";
import { generarTarjeta } from "@/lib/tarjeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const wrap = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] } } };

export default function Result({ data, onRestart, onBack }) {
  const rel = RELEVANCIA.find((r) => r.id === data.relevancia) || RELEVANCIA[0];
  const r = computeTarifa({
    seguidores: Number(data.seguidores) || 0,
    interacciones: Number(data.interacciones) || 0,
    autoridad: data.autoridad,
    produccion: data.produccion,
    relevancia: rel,
    cpl: data.cpl,
  });

  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [estado, setEstado] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [compartiendo, setCompartiendo] = useState("");
  const [usuario, setUsuario] = useState("");
  const [desgloseAbierto, setDesgloseAbierto] = useState(false);

  const gana = r.leverage >= r.tarifa;

  async function compartir(formato) {
    setCompartiendo(formato);
    try {
      const blob = await generarTarjeta({ r, data, usuario, formato });
      if (!blob) return;
      const nombre = formato === "story" ? "mi-tarifa-story-creatividad2.png" : "mi-tarifa-creatividad2.png";
      const file = new File([blob], nombre, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Mi tarifa de creador",
          text: `Mi piso por crear y postear un video: ${fmt$(r.tarifa)}. Calcula la tuya.`,
        });
        trackShareCard(formato);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
        trackShareCard(formato);
      }
    } catch {
      // el usuario canceló el diálogo de compartir
    } finally {
      setCompartiendo("");
    }
  }

  async function enviar(e) {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setErrorMsg("Escribe un correo válido, por ejemplo nombre@correo.com.");
      setEstado("error");
      document.getElementById("email-input")?.focus();
      return;
    }
    setEstado("sending");
    setErrorMsg("");
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 12000);
    try {
      const resp = await fetch(`${API}/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: ctrl.signal,
        body: JSON.stringify({
          email,
          website,
          usuario,
          pagina: window.location.href,
          tarifa: Math.round(r.tarifa),
          followers: Number(data.seguidores),
          engagement: Math.round(r.engagement * 10) / 10,
          interacciones: Number(data.interacciones),
          autoridad: data.autoridad,
          produccion: data.produccion,
          relevancia: rel.mult,
          cpl: data.cpl,
          leverage: Math.round(r.leverage),
          leads: Math.round(r.leads),
          piso_minimo_aplicado: r.piso,
        }),
      });
      clearTimeout(t);
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.ok === false) throw new Error("fallo");
      setEstado("exito");
      trackLead();
    } catch {
      clearTimeout(t);
      setEstado("error");
      setErrorMsg("No se pudo confirmar el envío. Tu correo sigue aquí: inténtalo de nuevo.");
    }
  }

  return (
    <section className="step" data-testid="resultado" style={{ justifyContent: "flex-start" }}>
      <div className="step-inner" style={{ maxWidth: 720 }}>
        <motion.div variants={wrap} initial="hidden" animate="show">
          <motion.div className="panel-negro" variants={item}>
            <div className="panel-texto">
              <p className="eyebrow">Tu piso · lo mínimo que cobras</p>
              <div className="precio display" data-testid="resultado-tarifa">
                <span className="marcador">{fmt$(r.tarifa)}</span>
              </div>
              <p className="precio-caption">
                Por crear y postear el video. Si te ofrecen menos, te están robando.
                {r.piso && <strong data-testid="piso-aplicado"> · Aplicado el mínimo de $150.</strong>}
              </p>
            </div>
            <span className="panel-foto" aria-hidden="true">
              <img src={`${process.env.PUBLIC_URL}/assets/img/foto-resultado.jpg`} alt="" />
            </span>
          </motion.div>

          <motion.button
            type="button"
            className="desglose-toggle"
            variants={item}
            onClick={() => setDesgloseAbierto((v) => !v)}
            aria-expanded={desgloseAbierto}
            data-testid="ver-desglose-btn"
          >
            <span>Ver desglose</span>
            <span aria-hidden="true">{desgloseAbierto ? "−" : "+"}</span>
          </motion.button>
          {desgloseAbierto && (
            <motion.div
              className="desglose"
              data-testid="resultado-desglose"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
            >
              <div className="desglose-row">
                <span>Base ({fmtNum(data.seguidores)} followers + engagement {r.engagement.toFixed(1)}%)</span>
                <b>{fmt$(r.base)}</b>
              </div>
              <div className="desglose-row">
                <span>× Autoridad</span>
                <b>×{data.autoridad}</b>
              </div>
              <div className="desglose-row">
                <span>× Producción</span>
                <b>×{data.produccion}</b>
              </div>
              <div className="desglose-row">
                <span>× Relevancia</span>
                <b>×{rel.mult}</b>
              </div>
            </motion.div>
          )}

          <motion.div className={`bloque-leverage${gana ? " gana" : ""}`} variants={item} data-testid="resultado-leverage">
            <p className="eyebrow">
              <span className="mk-estrella" aria-hidden="true" /> Lo que vales para ellos · vía CPL
            </p>
            <p className="lev-body">
              Basado en el CPL, tu audiencia vale <b>~{fmt$(r.leverage)}</b> para esta marca (≈{" "}
              <b>~{fmtNum(r.leads)} leads</b> × CPL ${data.cpl}).
            </p>
            <div className="nota-cpl">
              {gana
                ? `Pídeles hacia ${fmt$(r.leverage)}: tu tarifa se paga sola.`
                : "El CPL aquí es bajo. Defiende tu piso con relevancia, no en este número."}
            </div>
          </motion.div>

          <motion.p className="legal" variants={item} data-testid="legal-text">
            Esto <span className="no">NO</span> incluye Usage Rights, Whitelisting, Spark Ads ni
            Perpetuity Usage. Todo eso se cotiza aparte.
          </motion.p>

          <motion.div className="cap-form" variants={item}>
            <span className="mk-flecha" aria-hidden="true" />
            {estado === "exito" ? (
              <div className="exito" aria-live="polite" data-testid="email-exito">
                <strong>¡Listo! Te escribo pronto.</strong>
                Tu tarifa de {fmt$(r.tarifa)} va en camino a tu correo, con el desglose completo.
              </div>
            ) : (
              <>
                <p className="cap-title">¿Te gustó la herramienta?</p>
                <p className="cap-sub">
                  Déjame tu correo y te sigo mandando herramientas y cosas cool para creadores,
                  entrepreneurs y marcas.
                </p>
                <form onSubmit={enviar} noValidate data-testid="email-form">
                  <div className="email-row">
                    <input
                      id="email-input"
                      data-testid="email-input"
                      className="campo-input"
                      type="email"
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      spellCheck="false"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-label="Tu correo electrónico"
                    />
                    <button
                      type="submit"
                      className="btn btn-negro"
                      disabled={estado === "sending"}
                      data-testid="email-submit"
                    >
                      {estado === "sending" ? "Enviando…" : estado === "error" ? "Reintentar" : "Quiero"}
                    </button>
                  </div>
                  <div className="honeypot" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>
                  {errorMsg && (
                    <p className="error" role="alert" data-testid="email-error">
                      {errorMsg}
                    </p>
                  )}
                </form>
              </>
            )}
          </motion.div>

          <motion.div className="tarjeta-personaliza" variants={item}>
            <label htmlFor="usuario-input">Tu @usuario, si quieres que salga en la tarjeta (opcional)</label>
            <input
              id="usuario-input"
              data-testid="usuario-input"
              className="campo-input"
              type="text"
              inputMode="text"
              spellCheck="false"
              autoComplete="off"
              placeholder="@tuusuario"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
            />
          </motion.div>

          <motion.div className="resultado-acciones" variants={item}>
            <button type="button" className="backlink" onClick={onBack} data-testid="atras-btn">
              Atrás
            </button>
            <button type="button" className="btn btn-cta btn-sm" onClick={onRestart} data-testid="reiniciar-btn">
              Calcular otra
            </button>
            <button
              type="button"
              className="btn btn-cta btn-sm"
              onClick={() => compartir("feed")}
              disabled={compartiendo !== ""}
              data-testid="compartir-tarjeta-btn"
            >
              <span className="mk-estrella" aria-hidden="true" />
              {compartiendo === "feed" ? "Generando…" : "Compartir mi tarjeta"}
            </button>
            <button
              type="button"
              className="btn btn-cta btn-sm"
              onClick={() => compartir("story")}
              disabled={compartiendo !== ""}
              data-testid="compartir-story-btn"
            >
              <span className="mk-estrella" aria-hidden="true" />
              {compartiendo === "story" ? "Generando…" : "Compartir story"}
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
